import { useEffect, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useFlagsStatus, useUnleashClient } from "@unleash/proxy-client-react";
import { TRIAL_ONBOARDING_PREVIEW_UNLEASH_FLAG } from "@/lib/features";
import { useAppSelector } from "@/redux/hooks";
import {
  aplicarParametroOnboardingPreview,
  assinarOnboardingPreview,
  definirAvaliacaoOnboardingPreview,
  isOnboardingPreviewAtivo,
  isOnboardingPreviewPendente,
  resolverAvaliacaoOnboardingPreview,
  versaoOnboardingPreview,
  type RespostaFlagOnboardingPreview,
} from "@/lib/onboarding-preview";

/**
 * Fonte única do modo demonstração (chamado uma vez, no App).
 * Só aceita a resposta do Unleash calculada com o `userId` do usuário logado: o cache local
 * de toggles e a primeira busca sem userId deixavam a flag desligada e o modo intermitente.
 */
export function useSincronizarOnboardingPreview() {
  const client = useUnleashClient();
  const { flagsError } = useFlagsStatus();
  const userId = useAppSelector((state) => String(state.authReducer.id ?? "").trim());
  const { search } = useLocation();
  const [resposta, setResposta] = useState<RespostaFlagOnboardingPreview | null>(null);

  useEffect(() => {
    aplicarParametroOnboardingPreview(search);
  }, [search]);

  useEffect(() => {
    if (!client) return;
    // "update" só sai depois de uma busca no servidor (o cache local do init não emite).
    // Resposta sem userId no context não diz nada sobre a flag segmentada: é ignorada.
    const aoResponder = () => {
      const contextoUserId = String(client.getContext().userId ?? "").trim();
      if (!contextoUserId) return;
      setResposta({ userId: contextoUserId, liberada: client.isEnabled(TRIAL_ONBOARDING_PREVIEW_UNLEASH_FLAG) });
    };
    client.on("update", aoResponder);
    return () => {
      client.off("update", aoResponder);
    };
  }, [client]);

  useEffect(() => {
    definirAvaliacaoOnboardingPreview(
      resolverAvaliacaoOnboardingPreview({ userId, resposta, erroUnleash: Boolean(flagsError) }),
    );
  }, [userId, resposta, flagsError]);
}

function useVersaoOnboardingPreview() {
  useSyncExternalStore(assinarOnboardingPreview, versaoOnboardingPreview, versaoOnboardingPreview);
}

/** Modo demonstração ativo: flag avaliada para o usuário + `?onboardingPreview=1` na sessão. */
export function useOnboardingPreview(): boolean {
  useVersaoOnboardingPreview();
  return isOnboardingPreviewAtivo();
}

/** Pediu o modo e a flag ainda não chegou com o usuário. */
export function useOnboardingPreviewPendente(): boolean {
  useVersaoOnboardingPreview();
  return isOnboardingPreviewPendente();
}

/** Trial de verdade ou modo demonstração (usado só na Home e no guia). */
export function useIsEventoTrialOuPreview(isEventoTrial: boolean): {
  trial: boolean;
  preview: boolean;
  pendente: boolean;
} {
  const preview = useOnboardingPreview();
  const pendente = useOnboardingPreviewPendente();
  return {
    trial: isEventoTrial || preview,
    preview: preview && !isEventoTrial,
    pendente: pendente && !isEventoTrial,
  };
}
